/**
 * Canonical Generate Text reference-image counting (0–9).
 * Use at readiness, submit payload, Canvas connect guards, and UI labels.
 */

import { getNodeIO, isImageReferenceAssetHandle, type InputMaterial } from './dataflow'
import { resolveGenerateTextImageInputs } from './generateTextReadiness'

export const GENERATE_TEXT_MIN_REFERENCE_IMAGES = 0
export const GENERATE_TEXT_MAX_REFERENCE_IMAGES = 9

export function countGenerateTextReferenceImages(args: {
  fields: Record<string, unknown>
  inputMaterials?: InputMaterial[] | null
}): number {
  return resolveGenerateTextImageInputs(args).length
}

export function allGenerateTextReferenceImagesReady(args: {
  fields: Record<string, unknown>
  inputMaterials?: InputMaterial[] | null
}): boolean {
  const images = resolveGenerateTextImageInputs(args)
  if (images.length === 0) return true
  return images.every((img) => img.ready && Boolean(img.assetId || img.generatedOutputId))
}

export function isGenerateTextReferenceImageCountOverLimit(count: number): boolean {
  return count > GENERATE_TEXT_MAX_REFERENCE_IMAGES
}

function sourceNodeProvidesImage(nodeType: string | null | undefined): boolean {
  if (!nodeType) return false
  const io = getNodeIO(nodeType)
  return Boolean(io?.outputs.some((o) => o.dataType === 'IMAGE'))
}

interface GraphEdgeLike {
  source: string
  target: string
  targetHandle?: string | null
}

interface GraphNodeLike {
  id: string
  data: { nodeType?: string; fields?: Record<string, unknown> }
}

export function countGenerateTextImageReferenceEdges(
  edges: GraphEdgeLike[],
  nodes: GraphNodeLike[],
  targetId: string,
): number {
  let count = 0
  for (const edge of edges) {
    if (edge.target !== targetId) continue
    const handle = edge.targetHandle ?? null
    if (handle !== null && !isImageReferenceAssetHandle(handle)) continue
    const sourceNode = nodes.find((n) => n.id === edge.source)
    if (!sourceNode) continue
    if (sourceNodeProvidesImage(sourceNode.data.nodeType)) count += 1
  }
  return count
}

export function wouldExceedGenerateTextReferenceLimitOnConnect(args: {
  edges: GraphEdgeLike[]
  nodes: GraphNodeLike[]
  connection: { source?: string | null; target?: string | null; targetHandle?: string | null }
}): boolean {
  const targetId = args.connection.target
  const sourceId = args.connection.source
  if (!targetId || !sourceId) return false

  const targetNode = args.nodes.find((n) => n.id === targetId)
  if (targetNode?.data.nodeType !== 'generate_text') return false

  const handle = args.connection.targetHandle ?? null
  if (handle !== null && !isImageReferenceAssetHandle(handle)) return false
  // TEXT prompt edges do not consume the image budget.
  const sourceNode = args.nodes.find((n) => n.id === sourceId)
  if (!sourceNodeProvidesImage(sourceNode?.data.nodeType)) return false

  const currentCount = countGenerateTextImageReferenceEdges(args.edges, args.nodes, targetId)
  return currentCount >= GENERATE_TEXT_MAX_REFERENCE_IMAGES
}
